import { estimatePathDays } from "@/utils/pathEstimator";
import { computeTrueCost } from "@/utils/trueCostUtils";
import { getBenchmarkTier } from "@/utils/furyScore";
import { formatFirmDisplay } from "@/utils/formatFirmDisplay";

/**
 * Generate 2-3 data-driven "Why this firm fits" bullets for a ranking page.
 * All values derived from SSOT utilities — no hardcoded firm data.
 *
 * @param {string} intent - Page intent key (e.g., "fast-payouts", "under-100")
 * @param {object} firm   - Raw firm object from API
 * @returns {string[]}    - 2-3 short bullet strings
 */
export const getWhyFirmFits = (intent, firm) => {
  const fd = formatFirmDisplay(firm);
  const ptp = estimatePathDays(firm);
  const benchmarkTier = getBenchmarkTier(firm);
  const tc = benchmarkTier ? computeTrueCost(firm, benchmarkTier) : null;
  const bullets = [];

  const handlers = {
    "fast-payouts": () => {
      if (ptp?.total) bullets.push(`~${ptp.total} days to first payout`);
      if (fd.payoutSchedule) bullets.push(`${fd.payoutSchedule} payout schedule`);
      if (fd.profitSplit) bullets.push(`${fd.profitSplit} profit split`);
    },
    "daily-payouts": () => {
      if (fd.payoutSchedule) bullets.push(`${fd.payoutSchedule} payouts`);
      if (ptp?.total) bullets.push(`~${ptp.total} days to first withdrawal`);
      if (fd.profitSplit) bullets.push(`${fd.profitSplit} profit split`);
    },
    "under-100": () => {
      if (tc?.totalCost) bullets.push(`~$${tc.totalCost} total to funded (50K)`);
      if (fd.pricePrimary) bullets.push(`${fd.pricePrimary} evaluation fee`);
      const act = firm.activation_fee;
      if (act && (act === "$0" || act.toLowerCase() === "none")) bullets.push("No activation fee");
      else if (fd.profitSplit) bullets.push(`${fd.profitSplit} profit split`);
    },
    "under-200": () => {
      if (tc?.totalCost) bullets.push(`~$${tc.totalCost} total to funded (50K)`);
      if (fd.pricePrimary) bullets.push(`${fd.pricePrimary} evaluation fee`);
      if (fd.drawdownFull) bullets.push(`${fd.drawdownFull} drawdown`);
    },
    "cheapest": () => {
      if (tc?.totalCost) bullets.push(`~$${tc.totalCost} true cost (50K)`);
      if (fd.pricePrimary) bullets.push(`${fd.pricePrimary} eval fee`);
      const act = firm.activation_fee;
      if (act && (act === "$0" || act.toLowerCase() === "none")) bullets.push("No activation fee required");
      else if (fd.profitSplit) bullets.push(`${fd.profitSplit} profit split`);
    },
    "no-activation": () => {
      bullets.push("No activation fee required");
      if (tc?.totalCost) bullets.push(`~$${tc.totalCost} total to funded (50K)`);
      else if (fd.pricePrimary) bullets.push(`${fd.pricePrimary} evaluation fee`);
      if (ptp?.total) bullets.push(`~${ptp.total} days to first payout`);
    },
    "high-profit-split": () => {
      if (fd.profitSplit) bullets.push(`Up to ${fd.profitSplit} profit split`);
      if (fd.payoutSchedule) bullets.push(`${fd.payoutSchedule} payouts`);
      if (fd.drawdownFull) bullets.push(`${fd.drawdownFull} drawdown`);
    },
    "low-drawdown": () => {
      if (fd.drawdownFull) bullets.push(`${fd.drawdownFull} drawdown model`);
      if (firm.news_trading) bullets.push("News trading allowed");
      if (ptp?.total) bullets.push(`~${ptp.total} days to first payout`);
      else if (fd.profitSplit) bullets.push(`${fd.profitSplit} profit split`);
    },
  };

  const handler = handlers[intent];
  if (handler) handler();

  // Fallback: if handler produced < 2 bullets, pad with generic data
  if (bullets.length < 2) {
    if (fd.drawdownFull && !bullets.some(b => b.includes("drawdown"))) bullets.push(`${fd.drawdownFull} drawdown`);
    if (fd.profitSplit && !bullets.some(b => b.includes("split"))) bullets.push(`${fd.profitSplit} profit split`);
  }

  return bullets.slice(0, 3);
};
